/* Hidden Grounds: progressive, finite motion. All content is visible without JS. */
(() => {
  'use strict';

  const start = () => {
    const root = document.documentElement;
    if (root.dataset.hgMotionReady) return;
    root.dataset.hgMotionReady = 'true';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const buttons = [...document.querySelectorAll('[data-motion-toggle]')];
    const tiltCards = [...document.querySelectorAll('[data-tilt]')];
    const resetters = new Set();
    const running = new Map();
    const storageKey = 'hg-motion-paused';
    let userPaused = false;
    try { userPaused = localStorage.getItem(storageKey) === 'true'; } catch (_) { /* Optional preference. */ }
    const isPaused = () => reduceMotion.matches || userPaused;
    const permitted = () => !isPaused() && !document.hidden;

    // Each effect owns its timeout, listener and temporary SVG styles. A node
    // may reveal and draw together, so it can own more than one cleanup task.
    const trackEffect = (node, className, duration, cleanup, completed) => {
      if (!permitted()) { cleanup(); return; }
      const tasks = running.get(node) || new Set();
      running.set(node, tasks);
      let finished = false;
      let timer;
      const finish = event => { if (completed(event)) stop(); };
      const stop = () => {
        if (finished) return;
        finished = true;
        window.clearTimeout(timer);
        node.removeEventListener('animationend', finish);
        node.classList.remove(className);
        cleanup();
        tasks.delete(stop);
        if (!tasks.size) running.delete(node);
      };
      tasks.add(stop);
      node.addEventListener('animationend', finish);
      timer = window.setTimeout(stop, duration);
      node.classList.add(className);
    };

    const clearAnimations = () => {
      [...running.values()].forEach(tasks => [...tasks].forEach(stop => stop()));
      resetters.forEach(reset => reset());
    };

    const syncPreference = () => {
      const paused = isPaused();
      root.dataset.motion = paused ? 'paused' : 'active';
      root.classList.toggle('motion-enabled', !paused);
      buttons.forEach(button => {
        button.setAttribute('aria-pressed', String(paused));
        button.disabled = reduceMotion.matches;
        const label = reduceMotion.matches ? 'Reduced motion on' : paused ? 'Resume motion' : 'Pause motion';
        button.setAttribute('aria-label', label);
        button.title = reduceMotion.matches ? 'Your device is set to reduce motion.' : label;
        const labelNode = button.querySelector('[data-motion-label]');
        if (labelNode) labelNode.textContent = label;
        else button.textContent = label;
      });
      if (paused) clearAnimations();
    };

    buttons.forEach(button => {
      button.addEventListener('click', () => {
        if (reduceMotion.matches) return;
        userPaused = !userPaused;
        try { localStorage.setItem(storageKey, String(userPaused)); } catch (_) { /* Motion still pauses for this visit. */ }
        syncPreference();
      });
    });

    const onMediaChange = (query, handler) => {
      if (typeof query.addEventListener === 'function') query.addEventListener('change', handler);
      else if (typeof query.addListener === 'function') query.addListener(handler);
    };
    onMediaChange(reduceMotion, syncPreference);
    onMediaChange(finePointer, () => resetters.forEach(reset => reset()));
    syncPreference();

    const drawOnce = node => {
      if (!permitted()) return;
      const shapes = node.matches('path, circle, line, polyline, polygon, rect, ellipse')
        ? [node] : [...node.querySelectorAll('path, circle, line, polyline, polygon, rect, ellipse')];
      const drawing = new Set();
      let count = 0;
      shapes.forEach(shape => {
        if (typeof shape.getTotalLength !== 'function' || getComputedStyle(shape).stroke === 'none') return;
        try {
          const length = shape.getTotalLength();
          if (!Number.isFinite(length) || length <= 0) return;
          shape.style.setProperty('--stroke-length', String(length));
          shape.style.setProperty('--stroke-delay', `${Math.min(count * 55, 220)}ms`);
          shape.classList.add('motion-stroke');
          drawing.add(shape);
          count += 1;
        } catch (_) { /* Invalid SVG geometry remains static. */ }
      });
      if (!count) return;
      const pending = new Set(drawing);
      trackEffect(node, 'is-drawing', 1700, () => {
        drawing.forEach(shape => {
          shape.classList.remove('motion-stroke');
          shape.style.removeProperty('--stroke-length');
          shape.style.removeProperty('--stroke-delay');
        });
      }, event => {
        if (event.animationName !== 'hg-draw' || !pending.has(event.target)) return false;
        pending.delete(event.target);
        return pending.size === 0;
      });
    };

    const enter = node => {
      if (node.dataset.motionSeen === 'true') return;
      node.dataset.motionSeen = 'true';
      // Entries encountered while paused or hidden stay static, without a
      // surprise replay when the tab becomes visible or motion is resumed.
      if (!permitted()) return;
      if (node.hasAttribute('data-reveal')) {
        trackEffect(node, 'is-revealing', 1200, () => {}, event =>
          event.target === node && event.animationName === 'hg-reveal');
      }
      if (node.hasAttribute('data-draw')) drawOnce(node);
    };

    const revealNodes = document.querySelectorAll('[data-reveal], [data-draw]');
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          enter(entry.target);
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -24px 0px' });
      revealNodes.forEach(node => observer.observe(node));
    }

    tiltCards.forEach(card => {
      let frame = 0;
      let point = null;
      let glow = null;
      const reset = () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        point = null;
        card.classList.remove('is-tilting');
        card.style.removeProperty('--tilt-x');
        card.style.removeProperty('--tilt-y');
      };
      resetters.add(reset);
      card.addEventListener('pointermove', event => {
        if (!permitted() || !finePointer.matches || event.pointerType === 'touch') return;
        point = { x: event.clientX, y: event.clientY };
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          if (!point || !permitted() || !finePointer.matches) return;
          const rect = card.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const x = Math.max(0, Math.min(1, (point.x - rect.left) / rect.width));
          const y = Math.max(0, Math.min(1, (point.y - rect.top) / rect.height));
          if (!glow) {
            glow = document.createElement('span');
            glow.className = 'motion-pointer-glow';
            glow.setAttribute('aria-hidden', 'true');
            card.appendChild(glow);
          }
          card.style.setProperty('--tilt-x', `${((0.5 - y) * 6).toFixed(2)}deg`);
          card.style.setProperty('--tilt-y', `${((x - 0.5) * 6).toFixed(2)}deg`);
          card.style.setProperty('--pointer-x', `${(x * 100).toFixed(1)}%`);
          card.style.setProperty('--pointer-y', `${(y * 100).toFixed(1)}%`);
          card.classList.add('is-tilting');
        });
      }, { passive: true });
      card.addEventListener('pointerleave', reset, { passive: true });
      card.addEventListener('pointercancel', reset, { passive: true });
      card.addEventListener('focusout', reset);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) clearAnimations();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
